/**
 * Central route → access policy: the SINGLE source of truth for "who may see which route". The `_app` layout
 * guard resolves the session once and redirects BEFORE any gated query mounts, and route-error-classify shares
 * these same prefix sets as a reactive net. Keyed off the cached USER + route id rather than the thrown error
 * code, because a deferred-streamed query's rejection has its code redacted in production.
 */
import { pickIncompleteTestPath, type TestPath, type TestProgressUser } from './main-helpers'

// Route-id prefixes. Every route that may need a session renders under the `_app` shell; the `_public` shell's pages
// (the entry screens, the emailed links, the org survey) are never in these sets.

// Logged-out → /login. PUBLIC routes are intentionally EXCLUDED so they work without a session: the anonymous market
// report ('/_app/market').
export const AUTH_REQUIRED_ROUTE_PREFIXES = [
  '/_app/report',
  '/_app/teams',
  '/_app/jobs',
  '/_app/offers',
  '/_app/employees',
  '/_app/account',
  '/_app/orgs',
  '/_app/test',
]

// Routes an anonymous visitor may legitimately view. The top bar's "Compare my profile" CTA belongs ONLY here —
// elsewhere a null user means an EXPIRED session mid-redirect. Distinct from "not auth-required" so a future
// public route must opt in explicitly.
export const PUBLIC_ANONYMOUS_ROUTE_PREFIXES = ['/_app/market']

// First read gated on the applicant having finished the three questionnaires (the read rejects with
// TEST_INCOMPLETE). An applicant with the test still pending is sent to resume it.
export const TEST_GATED_ROUTE_PREFIXES = ['/_app/report', '/_app/teams', '/_app/jobs']

// Routes with no meaning in the COMPANY interface — the viewer's OWN personal report. An admin/authorized
// employee in company view (interfaceMode='company') is sent to their own EMPLOYEE CARD instead (mirrors
// getReport's EMPLOYEE_VIEW_ONLY, but landing on the profile they came to read rather than on the bare
// directory). The company home stays the fallback for a session with no id to address.
const COMPANY_VIEW_DENIED_ROUTE_PREFIXES = ['/_app/report']

// Company-management sections a self-registered professional cannot use. NOTE: '/_app/offers' is EXCLUDED (it
// serves applicants their OWN offers list) and '/_app/market' is public.
const COMPANY_CONTEXT_ROUTE_PREFIXES = ['/_app/jobs', '/_app/teams']

/** The cached `getUser` shape the policy reads (authOptional → null when logged out). */
export interface AccessUser extends TestProgressUser {
  // The viewer's own id. For an employee this IS their `employeeId` in the company's directory
  // (employees.employee_id references users.id), so it addresses their own card.
  id?: number
  type?: 'company' | 'applicant'
  isEmployee?: boolean
  interfaceMode?: 'employee' | 'company'
}

/** The redirect a route should perform, as a discriminated union of the (few, fixed) typed targets so
 *  the layout guard can render a type-safe `<Navigate>` per case. */
export type RouteRedirect =
  | { to: '/login'; redirect: string }
  | { to: '/employees' }
  | { to: '/employees/$employeeId/report'; employeeId: string }
  | { to: '/report' }
  | { to: TestPath }

const startsWithAny = (id: string, prefixes: readonly string[]) => prefixes.some((p) => id.startsWith(p))

/** True only on the public market / public-profile routes, where the anonymous "compare my profile" CTA
 *  is meaningful. Everywhere else a logged-out viewer is an expired session on its way to /login. */
export const isAnonymousComparableRoute = (routeId: string) => startsWithAny(routeId, PUBLIC_ANONYMOUS_ROUTE_PREFIXES)

const testPending = (u: TestProgressUser) => !(u.testPassed && u.testPassedValues && u.testPassedPersonality)

/** Order matters: logged-out → applicant with the test pending → company view → professional on a company page. */
export function resolveRouteAccess(input: {
  routeId: string
  user: AccessUser | null
  href: string
}): RouteRedirect | null {
  const { routeId, user, href } = input
  if (!user) return startsWithAny(routeId, AUTH_REQUIRED_ROUTE_PREFIXES) ? { to: '/login', redirect: href } : null
  if (user.type === 'applicant' && testPending(user) && startsWithAny(routeId, TEST_GATED_ROUTE_PREFIXES))
    return { to: pickIncompleteTestPath(user) }
  if (user.interfaceMode === 'company' && startsWithAny(routeId, COMPANY_VIEW_DENIED_ROUTE_PREFIXES))
    return user.id != null ? { to: '/employees/$employeeId/report', employeeId: String(user.id) } : { to: '/employees' }
  if (user.type === 'applicant' && !user.isEmployee && startsWithAny(routeId, COMPANY_CONTEXT_ROUTE_PREFIXES))
    return { to: '/report' }
  return null
}
